import type { BeforeFetchContext } from "@vueuse/core";
import type { GridStack, GridStackNode, GridStackWidget } from "gridstack";
import { defineStore } from "pinia";
import { v4 as uuid } from "uuid";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

import {
  useCreateDesks,
  useDeleteDesk,
  useSavedDesks,
  useSwitchDesk,
  useUpdateDesk,
} from "@/composition/api/desksApi.ts";
import useAuth from "@/composition/shared/useAuth.ts";
import { t } from "@/i18n.ts";
import { useLayoutStore } from "@/stores/layout.ts";
import type { ApiDesk, ApiWidget, NewApiDesk } from "@/types/desk.d.ts";
import delay from "@/utils/delay.ts";

export const useDeskStore = defineStore("desk", () => {
  const desks = ref<ApiDesk[]>([]);
  const { user } = useAuth();
  const loadingDesks = ref(true);
  const router = useRouter();

  const { execute: getSavedDesks, data: apiDesks } = useSavedDesks({
    initialData: [],
  });

  const grid = ref<GridStack | null>(null);

  /**
   * Serializes the current GridStack layout to a JSON string.
   * Only includes layout data (x, y, w, h, id)
   */
  function serializeLayout(): string | null {
    if (!grid.value) return null;
    const layout = grid.value.save();
    if (Array.isArray(layout)) {
      return JSON.stringify(
        layout.map(({ x, y, w, h, id }) => ({ x, y, w, h, id })),
      );
    }
    return null;
  }

  /**
   * Merges a layout array into the current desk's widgets.
   * Only updates position/size, never overwrites widget content.
   * Always replace the array reference for Vue reactivity.
   */

  function mergeLayoutIntoWidgets(layout: GridStackWidget[]) {
    const widgets = desks.value[selectedDeskIndex.value]?.widgets ?? [];
    desks.value[selectedDeskIndex.value].widgets = widgets.map((widget) => {
      const layoutItem = layout.find((l) => l.id === widget.id);
      if (
        layoutItem &&
        layoutItem.x !== undefined &&
        layoutItem.y !== undefined &&
        layoutItem.w !== undefined &&
        layoutItem.h !== undefined
      ) {
        return {
          ...widget,
          position: {
            x: layoutItem.x,
            y: layoutItem.y,
          },
          size: {
            w: layoutItem.w,
            h: layoutItem.h,
          },
        };
      }
      return widget;
    });
  }

  /**
   * Loads a serialized layout and merges it into widgets.
   * Does NOT call grid.value.load(layout) to avoid DOM conflicts.
   * @param {string | null} serializedLayout - The JSON string of the layout array.
   */
  function deserializeLayout(serializedLayout: string | null) {
    if (!serializedLayout) return;
    try {
      const layout = JSON.parse(serializedLayout);
      if (Array.isArray(layout)) {
        mergeLayoutIntoWidgets(layout);
      }
    } catch (e) {
      // eslint-disable-next-line no-console -- we want to log errors
      console.error("Failed to parse serializedLayout", e);
    }
  }

  /**
   * Fetches desks from the API and restores layout if available.
   */
  async function fetchDesks(
    options: Partial<{
      showLoading?: boolean;
      immediate?: boolean;
    }> = {},
  ) {
    const { showLoading = true, immediate = false } = options;

    if (showLoading) {
      loadingDesks.value = true;
    }
    try {
      // If not immediate for example on startup wait at least 500ms to show a loading spinner
      if (!immediate) {
        await Promise.all([getSavedDesks(), delay(500)]);
        desks.value = apiDesks.value ?? [];
      } else {
        await getSavedDesks();
        desks.value = apiDesks.value ?? [];
      }

      if (selectedDesk.value?.serializedLayout) {
        deserializeLayout(selectedDesk.value.serializedLayout);
      }
    } catch (e) {
      // eslint-disable-next-line no-console -- we want to log errors
      console.error(e);
    } finally {
      loadingDesks.value = false;
    }
  }

  const selectedDesk = computed<ApiDesk>(() => {
    return desks.value.find((desk) => desk.selected) ?? desks.value[0];
  });

  const selectedDeskIndex = computed(() => {
    return desks.value.findIndex((desk) => desk.selected) ?? 0;
  });

  const selectedDeskWidgets = computed(() => {
    return selectedDesk.value.widgets;
  });

  const isSharedDeskWithCurrentUser = computed(() => {
    if (!selectedDesk.value || !user.value) return;
    return (
      selectedDesk.value.shared &&
      selectedDesk.value.memberDpaIds.includes(user.value.account_id)
    );
  });

  const isSharedFromUser = computed(() => {
    if (!selectedDesk.value || !user.value) return;
    return selectedDesk.value.shareToken !== null;
  });

  const sharedWithUsers = computed(() => {
    if (!selectedDesk.value || !user.value) return;
    return selectedDesk.value.memberDpaIds.length;
  });

  const canWriteDesk = computed(() => {
    if (selectedDesk.value === null || !user.value) return;
    return selectedDesk.value.ownerDpaId === user.value.account_id;
  });

  const deskToUpdate = ref<ApiDesk | undefined>();
  const {
    execute: executeUpdateDesk,
    data: updatedDeskData,
    error: updateDeskError,
  } = useUpdateDesk(deskToUpdate, {
    beforeFetch: async (ctx: BeforeFetchContext) => {
      if (!selectedDesk.value) return ctx;
      const versionMismatch = await hasVersionMismatch(
        selectedDesk.value.id,
        selectedDesk.value.version,
      );
      if (versionMismatch) {
        ctx.cancel();
        showReloadSnackbar();
      }
    },
  });

  async function hasVersionMismatch(
    currentDeskId: string,
    expectedVersion: number,
  ): Promise<boolean> {
    await getSavedDesks();
    const remoteDesk = apiDesks.value?.find((d) => d.id === currentDeskId);
    if (!remoteDesk) return false;
    return remoteDesk.version !== expectedVersion;
  }

  function showReloadSnackbar() {
    const layoutStore = useLayoutStore();
    layoutStore.addSnackbarMessage(
      t("errors.desk.outOfSync"),
      "error",
      "alert",
      false,
      t("generic.pageRefresh"),
      () => window.location.reload(),
    );
  }

  async function updateDesk(
    desk?: ApiDesk,
    changedAttributes?: Partial<ApiDesk>,
  ): Promise<{ success: boolean; data?: ApiDesk; error?: string }> {
    if (!canWriteDesk.value) {
      return { success: false, error: "No write permissions for this desk" };
    }

    try {
      if (!desk) {
        deskToUpdate.value = selectedDesk.value;
      } else if (!changedAttributes) {
        deskToUpdate.value = desk;
      } else {
        deskToUpdate.value = {
          ...desk,
          ...changedAttributes,
        };
      }

      await executeUpdateDesk();
      const updatedDeskApi = updatedDeskData.value;

      if (!updatedDeskApi) {
        return {
          success: false,
          error: "Update failed - no data returned from server",
        };
      }

      // Update local state
      const oldDeskIndex = desks.value.findIndex(
        (oldDesk) => oldDesk.id === updatedDeskApi.id,
      );
      if (oldDeskIndex !== -1) {
        desks.value[oldDeskIndex] = updatedDeskApi;
      }

      return { success: true, data: updatedDeskApi };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  /**
   * Called in DeskGridstack whenever any number of widgets changed
   * Update widget positions/sizes and persist layout only.
   * @param widgets
   */
  type GridStackNodeOrArray = GridStackNode | GridStackNode[];

  async function updateWidgets(widgets: GridStackNodeOrArray) {
    // Always merge position/size into the full widget object
    const updatedWidgets = selectedDesk.value.widgets.map((widget) => {
      let updatedWidget;

      if (Array.isArray(widgets)) {
        updatedWidget = widgets.find(
          (gridstackWidget) => gridstackWidget.id === widget.id,
        );
      } else if (widget.id === widgets.id) {
        updatedWidget = widgets;
      }

      if (!updatedWidget) {
        return widget;
      }

      return {
        ...widget,
        position: {
          x: updatedWidget.x ?? widget.position?.x ?? 0,
          y: updatedWidget.y ?? widget.position?.y ?? 0,
        },
        size: {
          w: updatedWidget.w ?? widget.size?.w ?? 1,
          h: updatedWidget.h ?? widget.size?.h ?? 1,
        },
      };
    });

    desks.value[selectedDeskIndex.value].widgets = [...updatedWidgets];

    // Persist only layout to serializedLayout
    await updateDesk(selectedDesk.value, {
      serializedLayout: serializeLayout() ?? "",
    });
  }

  function updateWidget(widget?: GridStackNode) {
    if (!widget) return;
    const deskWidgetIndex = selectedDeskWidgets.value.findIndex(
      (iWidget) => iWidget.id === widget.id,
    );
    desks.value[selectedDeskIndex.value].widgets[deskWidgetIndex].position = {
      x: widget.x ?? selectedDeskWidgets.value[deskWidgetIndex].position.x,
      y: widget.y ?? selectedDeskWidgets.value[deskWidgetIndex].position.y,
    };
  }

  async function updateWidgetConfig(widgetIdentifier: string, newConfig: any) {
    const widgetIndex = selectedDeskWidgets.value.findIndex(
      (widget) => widget.id === widgetIdentifier,
    );

    if (widgetIndex === -1) return;

    desks.value[selectedDeskIndex.value].widgets[widgetIndex] = {
      ...desks.value[selectedDeskIndex.value].widgets[widgetIndex],
      attributes: newConfig,
    };

    desks.value[selectedDeskIndex.value].widgets = [
      ...desks.value[selectedDeskIndex.value].widgets,
    ];

    await updateDesk(selectedDesk.value, {
      widgets: desks.value[selectedDeskIndex.value].widgets,
    });
  }

  async function addNewWidget() {
    const newWidget = {
      id: uuid(),
      templateName: "NewWidgetAdder",
      attributes: {},
      isNew: true,
      label: "New Widget",
      position: { x: 0, y: 0 },
      size: { w: 3, h: 5 },
    };
    desks.value[selectedDeskIndex.value].widgets = [
      ...desks.value[selectedDeskIndex.value].widgets,
      newWidget,
    ];
  }

  async function removeWidget(widget: string | ApiWidget): Promise<void> {
    let widgetId;
    if (typeof widget === "string") {
      widgetId = widget;
    } else {
      widgetId = widget.id;
    }

    // Save original widgets for rollback
    const originalWidgets = [...desks.value[selectedDeskIndex.value].widgets];
    desks.value[selectedDeskIndex.value].widgets = desks.value[
      selectedDeskIndex.value
    ].widgets.filter((aWidget) => aWidget.id !== widgetId);

    desks.value[selectedDeskIndex.value].widgets = [
      ...desks.value[selectedDeskIndex.value].widgets,
    ];

    // Try to persist changes
    const result = await updateDesk(selectedDesk.value, {
      widgets: desks.value[selectedDeskIndex.value].widgets,
      serializedLayout: serializeLayout() ?? "",
    });

    if (!result.success) {
      // Rollback local changes if API update failed
      desks.value[selectedDeskIndex.value].widgets = originalWidgets;
    }
  }

  const isSwitchingDesks = ref(false);
  const deskToSwitch = ref<string | undefined>();
  const { execute: executeSwitchDesk, data: switchDeskData } =
    useSwitchDesk(deskToSwitch);
  async function switchDesks(
    newSelectedDesk: ApiDesk,
    options: Partial<{
      replaceRoute: boolean;
      showLoading?: boolean;
      minLoadingTime?: number;
      callback?: () => void;
    }> = { replaceRoute: false },
  ) {
    const {
      showLoading = false,
      replaceRoute = false,
      callback,
      minLoadingTime = 300,
    } = options;

    isSwitchingDesks.value = true;

    deskToSwitch.value = newSelectedDesk.id;
    // If we show a loading state we should at least wait 300ms otherwise it might look like a glitch to the user.
    if (showLoading) {
      loadingDesks.value = true;
      await Promise.all([executeSwitchDesk(), delay(minLoadingTime)]);
      desks.value = switchDeskData.value ?? [];
    } else {
      await executeSwitchDesk();
      desks.value = switchDeskData.value ?? [];
    }

    if (callback) {
      callback();
    }

    const routeOptions = {
      name: "DeskDetail",
      params: { id: newSelectedDesk.id },
    };

    await (replaceRoute
      ? router.replace(routeOptions)
      : router.push(routeOptions));

    if (showLoading) {
      loadingDesks.value = false;
    }
    isSwitchingDesks.value = false;

    if (selectedDesk.value?.serializedLayout) {
      deserializeLayout(selectedDesk.value.serializedLayout);
    }
  }

  const newDesk = ref<NewApiDesk | undefined>();
  const { execute: executeCreateDesk, data: newDeskData } =
    useCreateDesks(newDesk);
  async function createDesk(deskLabel: string) {
    newDesk.value = {
      label: deskLabel,
    };
    await executeCreateDesk();
    return newDeskData.value;
  }

  async function renameDesk(renamedDesk: ApiDesk) {
    deskToUpdate.value = renamedDesk;
    await executeUpdateDesk();

    if (updateDeskError.value) {
      return;
    }

    const updatedDesk = updatedDeskData.value;
    if (!updatedDesk) return;
    desks.value[selectedDeskIndex.value] = updatedDesk;
  }

  const deskToDelete = ref<string | undefined>();
  const { execute: executeDeleteDesk, error: deskDeleteError } = useDeleteDesk(
    deskToDelete,
    {
      beforeFetch: async (ctx: BeforeFetchContext) => {
        if (!selectedDesk.value) return ctx;
        const versionMismatch = await hasVersionMismatch(
          selectedDesk.value.id,
          selectedDesk.value.version,
        );
        if (versionMismatch) {
          ctx.cancel();
          showReloadSnackbar();
        }
      },
    },
  );

  async function deleteDesk(deskToDeleteParam: ApiDesk, callback?: () => void) {
    try {
      const deskToSwitchTo = Object.assign(
        {},
        desks.value[
          selectedDeskIndex.value === 0
            ? selectedDeskIndex.value + 1
            : selectedDeskIndex.value - 1
        ],
      );

      deskToDelete.value = deskToDeleteParam.id;

      await executeDeleteDesk();

      if (deskDeleteError.value) {
        deskToDelete.value = undefined;

        return;
      }

      if (callback) {
        callback();
      }
      await switchDesks(deskToSwitchTo, {
        showLoading: true,
      });
    } catch (error) {
      void error;
      deskToDelete.value = undefined;
    }
  }

  const joiningDesk = ref(false);

  return {
    desks,
    fetchDesks,
    loadingDesks,
    selectedDesk,
    selectedDeskIndex,
    selectedDeskWidgets,
    isSharedDeskWithCurrentUser,
    isSharedFromUser,
    canWriteDesk,
    switchDesks,
    isSwitchingDesks,
    sharedWithUsers,
    updateDesk,
    updateWidgets,
    updateWidget,
    updateWidgetConfig,
    createDesk,
    renameDesk,
    deleteDesk,
    deskDeleteError,
    updateDeskError,
    joiningDesk,
    addNewWidget,
    removeWidget,
    grid,
    serializeLayout,
    deserializeLayout,
    mergeLayoutIntoWidgets,
  };
});
